import React, { useState } from 'react';
import { X, Package as PkgIcon, Truck } from 'lucide-react';

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - deliveryPoints: Array<{ sku: string, title: string, quantity: number }>
 *  - data (optional): {
 *      items: Array<{ sku: string, title: string, quantity: number }>,
 *      deliveries: Array<{
 *        delivery_point_id: number,
 *        recipient_name: string,
 *        address: string,
 *        packages: Array<{
 *          package_id: number,
 *          tracking_code: string,
 *          items: Array<{ sku: string, title: string, quantity: number }>
 *        }>
 *      }>,
 *      counts?: { deliveries: number, packages: number, units: number }
 *    }
 */
function DeliveryInventoryDialog({ open, onClose, deliveryPoints = [], data }) {
  const [tab, setTab] = useState('summary'); // 'summary' | 'deliveries'
  
  if (!open) return null;

  // Use data if provided, otherwise fall back to deliveryPoints
  const items = data?.items || deliveryPoints;
  const deliveries = data?.deliveries || [];
  const totalUnits = data?.counts?.units ?? items.reduce((sum, it) => sum + (it.quantity || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl max-h-[90vh] flex flex-col">
        
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center shadow-md">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-[#B1336E] font-montserrat">
                  Envíos de Hoy
                </h3>
                <p className="text-sm text-slate-600 font-montserrat">
                  Productos programados para Fast Max
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-slate-200">
          <div className="inline-flex bg-slate-100 rounded-xl p-1 mb-4">
            <button
              onClick={() => setTab('summary')}
              className={`px-6 py-2.5 text-sm font-semibold rounded-lg font-montserrat transition-all duration-200 ${
                tab === 'summary'
                  ? 'bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Resumen
            </button>
            <button
              onClick={() => setTab('deliveries')}
              className={`px-6 py-2.5 text-sm font-semibold rounded-lg font-montserrat transition-all duration-200 ${
                tab === 'deliveries'
                  ? 'bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Por Entrega
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'summary' ? (
            <>
              {items.length > 0 ? (
                <>
                  {/* Desktop Table */}
                  <div className="hidden sm:block bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat uppercase tracking-wider">
                            SKU
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 font-montserrat uppercase tracking-wider">
                            Producto
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 font-montserrat uppercase tracking-wider">
                            Cantidad
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, idx) => (
                          <tr key={`${item.sku}-${idx}`} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-900 font-montserrat font-medium">
                                {item.sku || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-700 font-montserrat">
                                {item.title || item.sku || 'Sin título'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="inline-flex items-center justify-center px-3 py-1 bg-[#67A43C]/10 text-[#67A43C] text-sm font-bold rounded-full font-montserrat">
                                {item.quantity}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="sm:hidden space-y-3">
                    {items.map((item, idx) => (
                      <div
                        key={`${item.sku}-${idx}`}
                        className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="text-xs text-slate-500 font-montserrat mb-1 uppercase">
                              SKU
                            </div>
                            <div className="text-sm font-semibold text-slate-900 font-montserrat">
                              {item.sku || '—'}
                            </div>
                          </div>
                          <span className="px-3 py-1 bg-[#67A43C]/10 text-[#67A43C] text-sm font-bold rounded-full font-montserrat">
                            {item.quantity}
                          </span>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 font-montserrat mb-1 uppercase">
                            Producto
                          </div>
                          <div className="text-sm text-slate-700 font-montserrat">
                            {item.title || item.sku || 'Sin título'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Stats */}
                  <div className="mt-6 bg-gradient-to-r from-[#B1336E]/5 to-[#E6B43C]/5 rounded-xl p-4 border border-[#B1336E]/20">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-slate-600 font-montserrat mb-1 uppercase">
                          Productos (SKU)
                        </div>
                        <div className="text-2xl font-bold text-[#B1336E] font-montserrat">
                          {items.length}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-600 font-montserrat mb-1 uppercase">
                          Total Unidades
                        </div>
                        <div className="text-2xl font-bold text-[#B1336E] font-montserrat">
                          {totalUnits}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyState />
              )}
            </>
          ) : (
            <>
              {deliveries.length > 0 ? (
                <div className="space-y-4">
                  {deliveries.map((dp) => (
                    <div 
                      key={dp.delivery_point_id} 
                      className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                    >
                      {/* Delivery Point Header */}
                      <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-bold text-slate-900 font-montserrat">
                              {dp.recipient_name || 'Destinatario'}
                            </div>
                            <div className="text-sm text-slate-600 font-montserrat mt-0.5">
                              {dp.address}
                            </div>
                          </div>
                          <div className="px-2 py-1 bg-[#B1336E]/10 text-[#B1336E] text-xs font-bold rounded-lg font-montserrat">
                            DP #{dp.delivery_point_id}
                          </div>
                        </div>
                      </div>

                      {/* Packages */}
                      <div className="p-4 space-y-3">
                        {dp.packages.map((pkg) => (
                          <div 
                            key={pkg.package_id} 
                            className="rounded-lg border border-slate-200 overflow-hidden"
                          >
                            {/* Package Header */}
                            <div className="px-3 py-2 bg-slate-50 flex items-center justify-between border-b border-slate-200">
                              <div className="flex items-center gap-2">
                                <PkgIcon className="w-4 h-4 text-[#B1336E]" />
                                <span className="text-sm font-semibold text-slate-800 font-montserrat">
                                  Paquete #{pkg.package_id}
                                </span>
                              </div>
                              <div className="text-xs text-slate-500 font-montserrat">
                                {pkg.tracking_code}
                              </div>
                            </div>

                            {/* Package Items */}
                            <div className="p-3 bg-white">
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead>
                                    <tr className="text-xs uppercase text-slate-600 font-semibold font-montserrat border-b border-slate-200">
                                      <th className="text-left px-2 py-2">SKU</th>
                                      <th className="text-left px-2 py-2">Producto</th>
                                      <th className="text-right px-2 py-2">Cantidad</th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-sm divide-y divide-slate-100">
                                    {pkg.items.map((it, idx) => (
                                      <tr key={`${it.sku}-${idx}`} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-2 py-2 font-medium text-slate-900 font-montserrat">
                                          {it.sku || '—'}
                                        </td>
                                        <td className="px-2 py-2 text-slate-700 font-montserrat">
                                          {it.title || it.sku || 'Sin título'}
                                        </td>
                                        <td className="px-2 py-2 text-right">
                                          <span className="inline-flex items-center justify-center px-2 py-0.5 bg-[#67A43C]/10 text-[#67A43C] text-xs font-bold rounded-full font-montserrat">
                                            {it.quantity}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No hay entregas detalladas disponibles" />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white font-semibold py-3 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-montserrat"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <PkgIcon className="w-10 h-10 text-slate-300" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
        {message || 'No hay entregas para hoy'}
      </h3>
      <p className="text-sm text-slate-600 font-montserrat text-center max-w-sm">
        Los productos programados para entrega aparecerán aquí
      </p>
    </div>
  );
}

export default DeliveryInventoryDialog;